import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { SearchBox } from "../../../components/Search";
import { formatDateForAPI } from "../Utility/const";
import {
  getrestrictedAreaGssLoadDaily,
  getGssrestrictedAreaLoadMonthly,
  getRestrictedGssLoadDailyDiscom,
  getRestrictedMonthlyDiscomGssLoad,
  getRestrictedGssLoadDailyTotal,
  getRestrictedGssLoadMonthlyTotal,
  getGssLoadRestrictedDistrictDailyDiscom,
  getMonthlyRestrictedDistrictDiscomGssLoad,
} from "../Utility/RestApi";
import { useLegendFilter } from "../../../hooks/useLegendFilter";

const X_AXIS = [
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  { label: "Area", key: "area", values: ["area", "discom"] },
  { label: "Discom", key: "discom", values: ["discom"] },
  { label: "District", key: "district", values: ["district", "discom"] },
  { label: "Total", key: "total", values: ["total"] },
];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL", apiKey: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL", apiKey: "JVVNL" },
  { label: "AVVNL", key: "AVVNL", apiKey: "AVVNL" },
];

const KNOWN_DISCOMS = DISCOM.map((d) => d.apiKey); // ["JDVVNL", "JVVNL", "AVVNL"]

function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((displayKey) => {
    const discom = DISCOM.find((d) => d.key === displayKey);
    return discom ? discom.apiKey : displayKey;
  });
}

function getDisplayDiscomName(apiKey: string): string {
  const discom = DISCOM.find((d) => d.apiKey === apiKey);
  return discom ? discom.label : apiKey;
}

/**
 * Reliably splits a composite chart key (e.g. "33KVDRAWL_AVVNL", "AJMER_JVVNL")
 * by matching the known discom suffix, so underscores inside entity names are safe.
 */
function splitKeyByDiscom(key: string): {
  entityName: string;
  discomName: string;
} {
  for (const discom of KNOWN_DISCOMS) {
    if (key.endsWith(`_${discom}`)) {
      const entityName = key.slice(0, -(discom.length + 1));
      return { entityName, discomName: getDisplayDiscomName(discom) };
    }
  }
  return { entityName: key, discomName: "" };
}

function parseMonthYear(monthYearStr: string | number | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) return { month: 0, year: 0 };
  if (typeof monthYearStr === "number") return { month: monthYearStr, year: 0 };
  if (typeof monthYearStr === "string") {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const parts = monthYearStr.split("-");
    if (parts.length !== 2) return { month: 0, year: 0 };
    const [monthStr, yearStr] = parts;
    return {
      month: monthNames.indexOf(monthStr) + 1,
      year: 2000 + parseInt(yearStr),
    };
  }
  return { month: 0, year: 0 };
}

interface RajasthanDrawalPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: any[];
  onGroupByChange: (index: number) => void;
  selectedArea: any[];
  onAreaChange: (v: any) => void;
  selectedDistricts: any[];
  onDistrictsChange: (v: any) => void;
}

export function RajasthanDrawalPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedArea,
  onAreaChange,
  selectedDistricts,
  onDistrictsChange,
}: RajasthanDrawalPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const [areaTrendData, setAreaTrendData] = useState<any[]>([]);
  const [discomTrendData, setDiscomTrendData] = useState<any[]>([]);
  const [districtTrendData, setDistrictTrendData] = useState<any[]>([]);
  const [totalTrendData, setTotalTrendData] = useState<any[]>([]);

  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueArea, setUniqueArea] = useState<any[]>([]);
  const [uniqueDistrict, setUniqueDistrict] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  const _handlefetchData = useCallback(async () => {
    setLoading(true);
    try {
      const trendDates = DefaultDates[activeTab]?.defaultTrendDates;
      if (!trendDates || trendDates.length === 0) {
        setAreaTrendData([]);
        setDiscomTrendData([]);
        setDistrictTrendData([]);
        setTotalTrendData([]);
        setLoading(false);
        return;
      }

      const isMonthly = selectedXaxis.key === "month";

      const promises = trendDates.map(async (date: any) => {
        const [startDateStr, endDateStr] = date.value.split(",");
        if (!startDateStr || !endDateStr) return { area: [], discom: [], total: [], district: [] };

        const [areaRes, discomRes, totalRes, districtRes] = await Promise.all([
          isMonthly ? getGssrestrictedAreaLoadMonthly(startDateStr, endDateStr) : getrestrictedAreaGssLoadDaily(startDateStr, endDateStr),
          isMonthly ? getRestrictedMonthlyDiscomGssLoad(startDateStr, endDateStr) : getRestrictedGssLoadDailyDiscom(startDateStr, endDateStr),
          isMonthly ? getRestrictedGssLoadMonthlyTotal(startDateStr, endDateStr) : getRestrictedGssLoadDailyTotal(startDateStr, endDateStr),
          isMonthly ? getMonthlyRestrictedDistrictDiscomGssLoad(startDateStr, endDateStr) : getGssLoadRestrictedDistrictDailyDiscom(startDateStr, endDateStr),
        ]);

        return {
          area: Array.isArray(areaRes) ? areaRes.map(item => ({...item, dates: date.label})) : [],
          discom: Array.isArray(discomRes) ? discomRes.map(item => ({...item, dates: date.label})) : [],
          total: Array.isArray(totalRes) ? totalRes.map(item => ({...item, dates: date.label})) : [],
          district: Array.isArray(districtRes) ? districtRes.map(item => ({...item, dates: date.label})) : [],
        };
      });

      const results = await Promise.all(promises);
      setAreaTrendData(results.flatMap(r => r.area));
      setDiscomTrendData(results.flatMap(r => r.discom));
      setTotalTrendData(results.flatMap(r => r.total));
      setDistrictTrendData(results.flatMap(r => r.district));

    } catch (error) {
      console.error("Error in data fetching:", error);
      setAreaTrendData([]);
      setDiscomTrendData([]);
      setTotalTrendData([]);
      setDistrictTrendData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    let currentData: any[] = [];
    const groupKey = selectedGroupKeys[0]?.key;
    if (groupKey === "area") currentData = areaTrendData;
    else if (groupKey === "discom") currentData = discomTrendData;
    else if (groupKey === "total") currentData = totalTrendData;
    else if (groupKey === "district") currentData = districtTrendData;

    if (currentData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setUniqueDate([]);
      return;
    }

    if (selectedXaxis.key === "date") {
      setUniqueDate(
        Array.from(
          new Set(
            currentData
              .map((item: any) => item.date)
              .filter(Boolean)
              .sort(
                (a: string, b: string) =>
                  new Date(a).getTime() - new Date(b).getTime(),
              ),
          ),
        ),
      );
    } else {
      setUniqueDate(
        Array.from(
          new Set(currentData.map((item: any) => item.month).filter(Boolean)),
        ).sort((a: string, b: string) => {
          const am = parseMonthYear(a),
            bm = parseMonthYear(b);
          return am.year !== bm.year
            ? am.year - bm.year
            : am.month - bm.month;
        }),
      );
    }

    let filteredData = [...currentData];

    // Filter by discom
    if (selectedGroupKeys[0]?.key !== "total" && selectedDiscom.length > 0) {
      const apiDiscomKeys = getApiDiscomKeys(selectedDiscom);
      filteredData = filteredData.filter((item) =>
        apiDiscomKeys.includes(item.discom),
      );
    }

    // Filter by date or month
    if (selectedDate.length > 0) {
      const vals = selectedDate.map((d) => d.value);
      filteredData = filteredData.filter((item) =>
        selectedXaxis.key === "date"
          ? vals.includes(item.date)
          : vals.includes(item.month),
      );
    }

    // Build unique district labels
    const districtLabels = Array.from(
      new Set(
        filteredData.map((item) => {
          const name = item.district || item.area;
          const discomName = getDisplayDiscomName(item.discom);
          return name ? `${name} (${discomName})` : "";
        }),
      ),
    )
      .filter((label) => label !== "" && !label.includes("undefined"))
      .sort();
    setUniqueDistrict(districtLabels);

    // Filter by selected districts
    if (selectedDistricts.length > 0) {
      const distStrings = selectedDistricts.map((d: any) => d.value);
      filteredData = filteredData.filter((item) => {
        const name = item.district || item.area;
        const discomName = getDisplayDiscomName(item.discom);
        return distStrings.includes(`${name} (${discomName})`);
      });
    }

    // Build unique area labels
    const areaLabels = Array.from(
      new Set(
        filteredData.map((item) => {
          const name = item.area || item.station;
          const discomName = getDisplayDiscomName(item.discom);
          return name ? `${name} (${discomName})` : "";
        }),
      ),
    )
      .filter((label) => label !== "" && !label.includes("undefined"))
      .sort();
    setUniqueArea(areaLabels);

    // Filter by selected area
    if (selectedArea.length > 0) {
      const areaStrings = selectedArea.map((a: any) => a.value);
      filteredData = filteredData.filter((item) => {
        const name = item.area || item.station;
        const discomName = getDisplayDiscomName(item.discom);
        return areaStrings.includes(`${name} (${discomName})`);
      });
    }

    // Sort
    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        if (!a.month || !b.month) return 0;
        const am = parseMonthYear(a.month),
          bm = parseMonthYear(b.month);
        const aYear = am.year || a.year || 0,
          bYear = bm.year || b.year || 0;
        return aYear !== bYear ? aYear - bYear : am.month - bm.month;
      }
      if (!a.date || !b.date) return 0;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const convertedData = filteredData.map((item) => ({
      ...item,
      daily_peak_load_mw: (item.daily_peak_load_mw || 0) * conversionFactor,
      morning_peak_load_mw: (item.morning_peak_load_mw || 0) * conversionFactor,
      evening_peak_load_mw: (item.evening_peak_load_mw || 0) * conversionFactor,
      avg_daily_load_mw: (item.avg_daily_load_mw || 0) * conversionFactor,
    }));

    // groupKey is already defined at start of useEffect
    let groupByKeys: string[] = [];
    if (groupKey === "area") {
      groupByKeys = ["area", "discom"];
    } else if (groupKey === "district") {
      groupByKeys = ["district", "discom"];
    } else if (groupKey === "discom") {
      groupByKeys = ["discom"];
    } else if (groupKey === "total") {
      groupByKeys = ["total"];
    } else {
      groupByKeys = selectedGroupKeys.map((item) => item.key.toLowerCase());
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: groupByKeys,
      volumeKey: "",
      aggregationKeys: [
        "daily_peak_load_mw",
        "morning_peak_load_mw",
        "evening_peak_load_mw",
      ],
      averageKeys: [],
      averageDuplicationKeys: ["date"],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    areaTrendData,
    discomTrendData,
    districtTrendData,
    totalTrendData,
    selectedXaxis,
    selectedUnit,
    selectedDate,
    selectedDiscom,
    selectedGroupKeys,
    selectedArea,
    selectedDistricts,
  ]);

  const legends = useMemo(() => {
    const peakTypes = [
      { field: "daily_peak_load", displayName: "Day Peak" },
      { field: "morning_peak_load", displayName: "Morning Peak" },
      { field: "evening_peak_load", displayName: "Evening Peak" },
    ];

    if (!chartKeys?.length) return [];

    return chartKeys.flatMap((key) => {
      let displayKey = key;

      // ✅ AREA GROUPING
      if (selectedGroupKeys[0]?.key === "area") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const stationOrAreaKey = parts.slice(0, -1).join("_");

          const discomDisplayName = getDisplayDiscomName(discomKey);

          displayKey =
            stationOrAreaKey.toLowerCase() === "rajasthan"
              ? discomDisplayName
              : `${stationOrAreaKey} (${discomDisplayName})`;
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      }

      // ✅ DISTRICT GROUPING — shows "DistrictName (DISCOM)"
      else if (selectedGroupKeys[0]?.key === "district") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const districtKey = parts.slice(0, -1).join("_");

          const discomDisplayName = getDisplayDiscomName(discomKey);

          displayKey =
            !districtKey ||
            districtKey === "undefined" ||
            districtKey.trim() === ""
              ? discomDisplayName
              : `${districtKey} (${discomDisplayName})`;
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      }

      // ✅ DISCOM GROUPING
      else if (selectedGroupKeys[0]?.key === "discom") {
        displayKey = getDisplayDiscomName(key);
      }

      // ✅ TOTAL GROUPING
      else if (selectedGroupKeys[0]?.key === "total") {
        displayKey = "Total Demand";
      }

      // Generate peak legends
      return peakTypes.map((peak) => {
        const fieldSuffix = `${peak.field}_mw`;

        return {
          dataKey: `${key}_${fieldSuffix}`,
          name: `${displayKey} ${peak.displayName}`,
          legendColor: undefined,
          stroke: undefined,
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? peak.field : undefined,
        };
      });
    });
  }, [chartKeys, selectedGroupKeys, selectedChartType, getDisplayDiscomName]);

  function summaryExtraKeys() {
    return selectedXaxis.key === "date" ? ["date"] : ["month", "year"];
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={onDiscomChange}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => onGroupByChange(index)}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Restricted Demand Trend Peak" subtitle="Fetching Chhattisgarh restricted demand trend peak data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-100">
              Restricted Demand Peak Analysis ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              {selectedGroupKeys[0]?.key === "district" && (
                <div className="min-w-40">
                  <SearchBox
                    options={uniqueDistrict.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    isMany={true}
                    placeholder="Search District"
                    selectedOptions={selectedDistricts}
                    onChange={(v: any) => {
                      onDistrictsChange(v);
                      onAreaChange([]);
                    }}
                  />
                </div>
              )}
              {(selectedGroupKeys[0]?.key === "area" ||
                (selectedGroupKeys[0]?.key === "district" &&
                  selectedDistricts.length > 0)) && (
                <div className="min-w-40">
                  <SearchBox
                    options={uniqueArea.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    isMany={true}
                    placeholder="Search Area"
                    selectedOptions={selectedArea}
                    onChange={(v: any) => onAreaChange(v)}
                  />
                </div>
              )}
              <button
                onClick={() => {
                  if (dataTab.label === "Chart" || dataTab.label === "Table") {
                    downloadChartTableCSV(tablePar);
                  } else if (dataTab.label === "Summary") {
                    downloadSummaryCSV(tablePar);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              legends={legends.map((leg) => ({
                ...leg,
                type: selectedChartType.value,
                stackId: selectedChartType.stackId ?? undefined,
              }))}
              xDataKey={selectedXaxis.values[0]}
              secondXDataKey={undefined}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1. Sum of all GSS load = Actual drawal (Restricted demand) 
2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
